class DmvcsAddButton extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variantId;
    this.cartItems = new Set();
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    const addBtn = this.querySelector('.btn-add');
    this.originalContent = addBtn.textContent.trim();

    if (this.dataset.removeBtn) this.removeBtn = document.querySelector(this.dataset.removeBtn);
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
    this.initializeButtonState();

    if (this.removeBtn) {
      this.removeBtn.addEventListener('click', this.handleRemoveButton.bind(this));
    }

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source != 'product-form') {
        this.onCartUpdate(event);
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick.bind(this));

    if (this.removeBtn) {
      this.removeBtn.removeEventListener('click', this.handleRemoveButton.bind(this));
    }

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async initializeButtonState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.cartItems = new Set(cart.items.map((item) => item.variant_id.toString()));

      if (this.cartItems.has(this.variantId)) {
        this.updateButtonState('added');
      } else {
        this.updateButtonState('add');
      }
    } catch (error) {
      console.error('Error fetching cart state:', error);
    }
  }

  onCartUpdate(event) {
    const variantsRemoved = new Set(event.cartData.items_removed.map((item) => item.variant_id.toString()));
    if (variantsRemoved.has(this.variantId)) {
      this.cartItems.delete(this.variantId);
      this.updateButtonState('add');
      this.toggleRemoveButton('off');
    }
  }

  updateButtonState(state) {
    const addedBtn = this.querySelector('.btn-added');
    const addBtn = this.querySelector('.btn-add');

    if (!addedBtn || !addBtn) return;

    if (state === 'add') {
      addBtn.textContent = this.originalContent;
      addBtn.classList.remove('xhidden');
      addBtn.classList.add('xflex');
      addedBtn.classList.add('xhidden');
      addedBtn.classList.remove('xflex');
      this.disabled = false;
    } else if (state === 'adding') {
      addBtn.textContent = 'Adding...';
      addBtn.classList.remove('xhidden');
      addBtn.classList.add('xflex');
      addedBtn.classList.add('xhidden');
      addedBtn.classList.remove('xflex');
      this.disabled = true;
    } else if (state === 'added') {
      addBtn.classList.add('xhidden');
      addBtn.classList.remove('xflex');
      addedBtn.classList.remove('xhidden');
      addedBtn.classList.add('xflex');
      this.disabled = false;
    }
  }

  toggleRemoveButton(state) {
    if (!this.removeBtn) return;

    if (state == 'on') {
      this.removeBtn.classList.remove('xhidden');
      this.removeBtn.classList.add('xflex');
    } else {
      this.removeBtn.classList.add('xhidden');
      this.removeBtn.classList.remove('xflex');
    }
  }

  async handleClick(event) {
    event.preventDefault();

    if (!this.variantId) {
      console.error('No variant ID found on button');
      return;
    }

    if (this.cartItems.has(this.variantId)) {
      this.cart.open();
    } else {
      this.handleAddToCart(event);
    }
  }

  async handleAddToCart(event) {
    event.preventDefault();

    try {
      this.disabled = true;
      this.updateButtonState('adding');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: this.variantId,
              quantity: 1,
            },
          ],
        }),
      });

      if (!response.ok) throw new Error('Add to cart failed');

      // remove is empty from cart
      this.cart.classList.remove('is-empty');

      this.cartItems.add(this.variantId.toString());
      this.updateButtonState('added');

      // check for remove BTN
      if (this.removeBtn) this.toggleRemoveButton('on');

      // Update cart UI
      if (this.cart) {
        const cartContent = await this.getCartContent();
        this.cart.renderContents(cartContent);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.updateButtonState('add');
      this.disabled = false;
    }
  }

  async getCartContent() {
    const response = await fetch('?section_id=cart-drawer');
    const html = await response.text();
    const parser = new DOMParser();
    const cartSection = parser.parseFromString(html, 'text/html').querySelector('[id^="shopify-section-"]');

    return {
      sections: {
        'cart-drawer': cartSection.innerHTML,
        'cart-icon-bubble': await this.getCartIconBubble(),
      },
    };
  }

  async getCartIconBubble() {
    const response = await fetch('?section_id=cart-icon-bubble');
    const html = await response.text();
    return html;
  }

  async handleRemoveButton(event) {
    event.preventDefault();
    event.stopPropagation();

    try {
      this.removeBtn.disabled = true;

      // Find the cart item to remove
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const itemToRemove = cart.items.find((item) => item.variant_id.toString() === this.variantId);

      if (!itemToRemove) throw new Error('Item not found in cart');

      const body = JSON.stringify({
        id: itemToRemove.key,
        quantity: 0,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'dmvcs-remove-btn',
            cartData: parsedState,
            variantId: this.variantId,
          });
        })
        .catch((error) => {
          console.error('Error removing from cart:', error);
          this.removeBtn.disabled = false;
        });
    } catch (error) {
      console.error('Error removing from cart:', error);
      this.removeBtn.disabled = false;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('dmvcs-add-btn', DmvcsAddButton);
